import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';

// ─── Integration Registry ────────────────────────────────────────────────────
// To add a new integration: push a new object into this array.
const INTEGRATIONS = [
  {
    id: 'emdash',
    label: 'EmDash Plugin',
    badge: 'Available on npm',
    badgeColor: 'text-green-400 bg-green-400/10 border-green-400/20',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 10V11" />
      </svg>
    ),
    tagline: 'AI chatbot plugin for EmDash CMS',
    description: 'Drop the InjectAI RAG engine into any EmDash + Astro site. Upload your content once — your visitors get an AI assistant that answers questions grounded in that content, no hallucinations.',
    features: [
      'Install with a single npm command',
      'Supports PDFs, videos, audio, DOCX, PPTX, CSV and more',
      'Files up to 5GB supported',
      'Live Chat Designer — colors, logo, floating or full-page mode',
      'Multi-turn conversation memory with session history',
      'Multiple AI providers: OpenAI, Anthropic, Gemini, Groq, Cerebras',
      'Admin panel: upload, manage files, generate quizzes & modules',
    ],
    npmPackage: '@injectailabs/emdash-injectai',
    npmUrl: 'https://www.npmjs.com/package/@injectailabs/emdash-injectai',
    marketplaceLabel: null,
    marketplaceUrl: null,
    downloadLabel: null,
    downloadUrl: null,
    docsLabel: 'Get in touch',
    docsUrl: '/contact',
    reviewNotice: null,
  },
  {
    id: 'wordpress',
    label: 'WordPress Plugin',
    badge: 'Under Review by WordPress',
    badgeColor: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zM3.5 12c0-1.232.256-2.405.712-3.469L7.93 19.21A8.5 8.5 0 013.5 12zm8.5 8.5a8.46 8.46 0 01-2.4-.347l2.548-7.4 2.61 7.153a.75.75 0 00.057.11A8.46 8.46 0 0112 20.5zm1.172-11.632l2.16 6.457.598-1.997 1.03-2.46c.456-1.085.608-1.952.608-2.724 0-.28-.018-.54-.05-.784a8.5 8.5 0 01-4.346 1.508zm-2.344-.13a8.5 8.5 0 01-3.828-.97c.49 1.63 1.36 3.07 2.5 4.24l1.328-3.27zm7.5 1.262c0 .9-.167 1.91-.668 3.175l-.874 2.918-3.16-9.4a8.5 8.5 0 014.702 3.307z" />
      </svg>
    ),
    tagline: 'AI Chatbot for any WordPress site',
    description: 'Install the InjectAI plugin and add a fully-featured RAG chatbot to any WordPress site in minutes. No coding required.',
    features: [
      'Chat with videos, PDFs, audio, documents and spreadsheets',
      'Supports files up to 5GB',
      'Live chat designer — colors, logo, layout, suggestion chips',
      'Multi-turn conversation memory with session history',
      'Multiple AI providers: OpenAI, Anthropic, Gemini, Groq, Cerebras',
      'One shortcode: [injectai_chat] on any page or post',
      'Floating widget or full-page embed modes',
    ],
    marketplaceLabel: 'WordPress.org Plugin Directory',
    marketplaceUrl: 'https://wordpress.org/plugins/injectai',
    downloadLabel: 'Download Plugin',
    downloadUrl: 'https://wordpress.org/plugins/injectai',
    docsLabel: 'Get in touch to access',
    docsUrl: '/contact',
    reviewNotice: 'Currently under review by WordPress.org. Need immediate access? Reach out directly.',
  },
  {
    id: 'moodle',
    label: 'Moodle Plugin',
    badge: 'Under Review by Moodle',
    badgeColor: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
    icon: (
      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    tagline: 'AI learning assistant for Moodle LMS',
    description: 'A Moodle block plugin that embeds an AI chatbot trained on your course content — helping students get instant, contextual answers.',
    features: [
      'Block plugin — installs via standard Moodle plugin manager',
      'Course-aware RAG: answers grounded in uploaded course materials',
      'Per-course content isolation for accurate, relevant responses',
      'Supports video lectures, PDFs, slides and documents',
      'Configurable AI provider and model per site',
      'Admin panel for API key management and content uploads',
      'Works with Moodle 4.x and above',
    ],
    marketplaceLabel: 'Moodle Plugin Directory',
    marketplaceUrl: 'https://moodle.org/plugins',
    downloadLabel: 'Download Plugin',
    downloadUrl: 'https://moodle.org/plugins',
    docsLabel: 'Get in touch to access',
    docsUrl: '/contact',
    reviewNotice: 'Currently under review by Moodle.org. Need immediate access? Reach out directly.',
  },
  {
    id: 'api',
    label: 'REST API',
    badge: 'Production',
    badgeColor: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
    tagline: 'Integrate the RAG engine into any platform',
    description: 'A clean REST API that exposes the full InjectAI RAG engine. Upload content, manage tenants, run queries — all programmatically.',
    features: [
      'Upload, manage and delete documents via API — full content lifecycle control',
      'Streaming and non-streaming chat endpoints',
      'Multi-tenant support with metadata-based isolation',
      'Webhook support for async processing status',
      'API key authentication with per-key rate limiting',
      'Supports all file types: video, audio, PDF, DOCX, XLS, images',

    ],
    marketplaceLabel: null,
    marketplaceUrl: null,
    downloadLabel: null,
    downloadUrl: null,
    docsLabel: 'View API Documentation',
    docsUrl: '/contact',
    reviewNotice: null,
  },
];

// ─── Card Component ───────────────────────────────────────────────────────────
const IntegrationCard = ({ integration }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={`bg-dark border rounded-2xl transition-all duration-300 ${open ? 'border-primary/50' : 'border-gray-800 hover:border-gray-700'}`}>
      {/* Card header — always visible */}
      <button
        className="w-full text-left p-6 md:p-8 flex items-start gap-5"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${open ? 'bg-primary/20 text-primary' : 'bg-gray-800 text-gray-400'}`}>
          {integration.icon}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <h3 className="text-xl font-bold text-white">{integration.label}</h3>
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${integration.badgeColor}`}>
              {integration.badge}
            </span>
          </div>
          <p className="text-gray-400 text-sm">{integration.tagline}</p>
        </div>

        <div className={`flex-shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center transition-all ${open ? 'border-primary/40 bg-primary/10 text-primary rotate-180' : 'border-gray-700 text-gray-500'}`}>
          <svg className="w-4 h-4 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded content */}
      {open && (
        <div className="px-6 md:px-8 pb-8 border-t border-gray-800/60">
          <p className="text-gray-300 mt-6 mb-6 leading-relaxed">{integration.description}</p>

          {/* Features */}
          <div className="mb-8">
            <h4 className="text-xs uppercase tracking-widest text-gray-500 mb-4 font-medium">Features</h4>
            <ul className="grid sm:grid-cols-2 gap-2">
              {integration.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-gray-300">
                  <svg className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {f}
                </li>
              ))}
            </ul>
          </div>

          {/* Review notice */}
          {integration.reviewNotice && (
            <div className="flex items-start gap-3 p-4 bg-yellow-400/5 border border-yellow-400/20 rounded-xl mb-6">
              <svg className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <p className="text-sm text-yellow-300/80">{integration.reviewNotice}</p>
            </div>
          )}

          {/* npm install snippet */}
          {integration.npmPackage && (
            <div className="mb-6">
              <h4 className="text-xs uppercase tracking-widest text-gray-500 mb-3 font-medium">Install</h4>
              <div className="flex items-center gap-3 bg-gray-900 border border-gray-700 rounded-xl px-5 py-3 font-mono text-sm text-green-400">
                <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M0 0h24v24H0V0zm19.2 4.8H4.8C3.81 4.8 3 5.61 3 6.6v10.8c0 .99.81 1.8 1.8 1.8h14.4c.99 0 1.8-.81 1.8-1.8V6.6c0-.99-.81-1.8-1.8-1.8zm-9.6 10.8H7.8V9.6h1.8v6zm4.8 0h-1.8V9.6h1.8v6zm4.8 0h-3.6V9.6h1.8v4.2h1.8v-4.2h1.8v6z"/>
                </svg>
                <span>npm install {integration.npmPackage}</span>
                <a
                  href={integration.npmUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto text-xs text-gray-500 hover:text-primary transition-colors whitespace-nowrap"
                >
                  View on npm ↗
                </a>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            {integration.downloadUrl && (
              <a
                href={integration.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-blue-600 rounded-xl text-sm font-semibold transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {integration.downloadLabel}
              </a>
            )}
            {integration.marketplaceUrl && (
              <a
                href={integration.marketplaceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-700 hover:border-primary hover:bg-primary/5 rounded-xl text-sm font-semibold text-gray-300 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {integration.marketplaceLabel}
              </a>
            )}
            {integration.docsUrl && (
              <Link
                to={integration.docsUrl}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-blue-600 rounded-xl text-sm font-semibold transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                {integration.docsLabel}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Section ──────────────────────────────────────────────────────────────────
const Integrations = () => {
  return (
    <>
      <SEO
        title="Integrations"
        path="/integrations"
        description="InjectAI RAG engine available as a WordPress plugin, Moodle plugin, and REST API. Integrate AI into your platform without a full rebuild."
      />
      <section id="integrations" className="py-24 pt-36 bg-dark">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 border border-primary/20 rounded-full text-xs text-primary font-medium mb-5 uppercase tracking-widest">
            Integrations
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
            Available Everywhere
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl">
            The InjectAI RAG engine ships as plugins for major platforms and as a direct API. Click any integration to explore features, documentation and download links.
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {INTEGRATIONS.map(integration => (
            <IntegrationCard key={integration.id} integration={integration} />
          ))}
        </div>

        <p className="mt-8 text-sm text-gray-600 text-center">
          More integrations coming soon
        </p>
      </div>
    </section>
    </>
  );
};

export default Integrations;
